import type { FirebaseOptions } from 'firebase/app';

/**
 * Centralised client configuration. Everything comes from Vite env (`VITE_*`,
 * see `.env.example`); nothing is hard-coded. These are public-by-design
 * Firebase web values — the RTDB Security Rules are what actually gate access —
 * but we still keep them in env, never embed a privileged secret.
 *
 * This module must NOT throw at import time: a missing var should surface as a
 * friendly "configuration required" screen (see main.tsx + ConfigError), not a
 * blank page from an uncaught module-load error.
 */

const env = import.meta.env;

/** A value counts as "unset" if it's blank or still the .env.example placeholder. */
const isUnset = (value: string | undefined): boolean =>
  !value || value.trim() === '' || value.startsWith('replace-with');

const REQUIRED: ReadonlyArray<{ name: string; value: string | undefined }> = [
  { name: 'VITE_FIREBASE_API_KEY', value: env.VITE_FIREBASE_API_KEY },
  { name: 'VITE_FIREBASE_AUTH_DOMAIN', value: env.VITE_FIREBASE_AUTH_DOMAIN },
  { name: 'VITE_FIREBASE_DATABASE_URL', value: env.VITE_FIREBASE_DATABASE_URL },
  { name: 'VITE_FIREBASE_PROJECT_ID', value: env.VITE_FIREBASE_PROJECT_ID },
  { name: 'VITE_FIREBASE_APP_ID', value: env.VITE_FIREBASE_APP_ID },
  { name: 'VITE_FIREBASE_PATH', value: env.VITE_FIREBASE_PATH },
];

/** Names of required env vars that are missing/placeholder. Empty ⇒ good to go. */
export const missingEnvVars: string[] = REQUIRED.filter((v) => isUnset(v.value)).map(
  (v) => v.name,
);

export const firebaseConfig: FirebaseOptions = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: env.VITE_FIREBASE_DATABASE_URL,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  appId: env.VITE_FIREBASE_APP_ID,
  ...(env.VITE_FIREBASE_STORAGE_BUCKET
    ? { storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET }
    : {}),
  ...(env.VITE_FIREBASE_MESSAGING_SENDER_ID
    ? { messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID }
    : {}),
};

/** Publisher root the snapshots live under (legacy "firebasePath"), e.g. "museum". */
export const firebasePath: string = env.VITE_FIREBASE_PATH;

/**
 * Direct-download base for snapshot images, e.g.
 * `https://storage.googleapis.com/cr7-interactive.appspot.com`. Snapshot URLs
 * in RTDB use the short cdn.museucr7.com redirect host (keeps printed QRs
 * small), but its 301s carry no CORS headers — and even if they did, a
 * cross-origin redirect blanks the fetch's origin to `null`, which the
 * bucket's CORS allowlist rejects. Anything that needs a CORS fetch (Save)
 * must therefore hit the bucket directly; the redirect site serves the same
 * path on this base (see cloud/cdn/README.md). The bucket is always the
 * project's `<projectId>.appspot.com` — the same convention the CDN redirect
 * config and the functions' CI hard-code. Deliberately NOT derived from
 * VITE_FIREBASE_STORAGE_BUCKET: that's the web-app SDK field, and consoles
 * now hand out the `<projectId>.firebasestorage.app` alias there, which is
 * not where the snapshot images live.
 */
export const storageBaseUrl: string = env.VITE_FIREBASE_PROJECT_ID
  ? `https://storage.googleapis.com/${env.VITE_FIREBASE_PROJECT_ID}.appspot.com`
  : '';

/**
 * How many days back the backoffice lets you browse (today included). Snapshot
 * *images* are deleted by a Cloud Storage lifecycle rule once they pass this age
 * (cloud/functions/lifecycle.json) — the RTDB records are kept, but their images
 * are gone, so navigating to an older day would only show broken thumbnails and
 * dead print links. Capping the navigator to the last `retentionDays` days keeps
 * the view to days whose images still exist.
 *
 * MUST be kept equal to the lifecycle rule's `age`. Default 7; override with
 * VITE_RETENTION_DAYS. A missing/invalid value falls back to 7.
 */
export const retentionDays: number = (() => {
  const raw = env.VITE_RETENTION_DAYS;
  const n = raw && raw.trim() !== '' ? Number(raw) : NaN;
  return Number.isInteger(n) && n > 0 ? n : 7;
})();

/**
 * Where the "Take a snapshot" button writes the kiosk player's capture trigger.
 * Mirrors the player's own path construction so both
 * agree: an empty location is the single kiosk at `<fp>/trigger`; a location id
 * namespaces a 2nd kiosk to `<fp>/triggers/<location>`. Keep
 * VITE_PLAYER_LOCATION in sync with the player's env.
 */
const playerLocation: string = (env.VITE_PLAYER_LOCATION ?? '').trim();
export const triggerPath: string = playerLocation
  ? `${firebasePath}/triggers/${playerLocation}`
  : `${firebasePath}/trigger`;

/**
 * The kiosk's heartbeat/state node: `{ status, online,
 * updatedAt }`, written by the player so the backoffice can see readiness/health.
 * Same location convention as {@link triggerPath}: `<fp>/player/state` for the
 * single kiosk, `<fp>/players/<location>/state` when namespaced.
 */
export const playerStatePath: string = playerLocation
  ? `${firebasePath}/players/${playerLocation}/state`
  : `${firebasePath}/player/state`;

/**
 * The kiosk's admin-set feature switches: currently
 * `{ qrTrigger: boolean }` — the on/off switch for the idle banner's
 * phone-trigger QR. Admin-only write by rule; the kiosk subscribes and reacts
 * live (off ⇒ QR hidden, photographed codes refused). Same location
 * convention as {@link playerStatePath}.
 */
export const playerSettingsPath: string = playerLocation
  ? `${firebasePath}/players/${playerLocation}/settings`
  : `${firebasePath}/player/settings`;

/**
 * The animation (frame) code sent with each trigger. Defaults to the `auto`
 * sentinel: the kiosk picks the next pose in its own rotation, so repeated
 * "Take a snapshot" taps cycle the poses sequentially. Override with
 * VITE_TRIGGER_ANIMATION to pin one pose — the value MUST then be a code the
 * snapshots function knows (cloud/functions/src/media/frames.ts, mirrored in
 * apps/player/src/lib/animations.ts); the kiosk discards a trigger whose code
 * it doesn't recognise.
 */
export const triggerAnimation: string =
  env.VITE_TRIGGER_ANIMATION && env.VITE_TRIGGER_ANIMATION.trim() !== ''
    ? env.VITE_TRIGGER_ANIMATION.trim()
    : 'auto';

/**
 * The pose codes offered by the long-press chooser on "Take a snapshot" (a
 * plain tap sends {@link triggerAnimation}). Comma-separated codes in
 * VITE_TRIGGER_ANIMATIONS; defaults to the kiosk's current clip line-up
 * (apps/player/public/videos/manifest.json). Every code MUST be one the
 * snapshots function knows — the kiosk plays a fallback clip for a code its
 * manifest doesn't map, though the photo still frames correctly.
 */
export const triggerAnimationChoices: readonly string[] = (() => {
  const list = (env.VITE_TRIGGER_ANIMATIONS ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  return list.length > 0 ? list : ['por/uma-mao', 'por/bracos-cruzados'];
})();

/**
 * How the Print button fulfils a print:
 *  - `'browser'` (DEFAULT) — open the full-size image in a new tab; the user
 *    saves it to the gallery or prints it from there. Works everywhere and
 *    needs no RTDB write.
 *  - `'agent'` — enqueue a job in RTDB for the on-prem print agent
 *    ({@link PrintDispatch}). Requires the `printJobs` write rule + a running
 *    agent, so it's **opt-in per environment** via the dotenv.
 *
 * Defaulting to `'browser'` means an environment that hasn't provisioned the
 * agent (e.g. production) keeps working unchanged — enabling `'agent'` is a
 * deliberate config choice, never the default.
 */
export const printMode: 'browser' | 'agent' =
  env.VITE_PRINT_MODE === 'agent' ? 'agent' : 'browser';

/**
 * Which on-prem printer/location print jobs are routed to (agent mode only).
 * Jobs are written to `<firebasePath>/printJobs/<printLocation>/<pushKey>`; the
 * on-prem agent must listen at the same path (its `PRINT_JOBS_PATH`). Defaults
 * to `default`.
 */
export const printLocation: string =
  env.VITE_PRINT_LOCATION && env.VITE_PRINT_LOCATION.trim() !== ''
    ? env.VITE_PRINT_LOCATION.trim()
    : 'default';

/**
 * The continue/completion URL the email sign-in link returns to. Defaults to the
 * current origin (so the same deploy that sent the link receives it). Must be a
 * Firebase Auth → Authorized domain.
 */
export const signInContinueUrl = (): string => {
  const configured = env.VITE_SIGNIN_CONTINUE_URL;
  if (configured && configured.trim() !== '') return configured;
  return window.location.origin + window.location.pathname;
};
